import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface DauMauPoint {
  readonly date: string;
  readonly dau: number;
  readonly mau: number;
  readonly stickiness: number;
}

interface DauMauResponse {
  readonly configured: boolean;
  readonly points: readonly DauMauPoint[];
}

interface EventCount {
  readonly event: string;
  readonly count: number;
}

interface ScreenView {
  readonly screen: string;
  readonly count: number;
}

interface EventsResponse {
  readonly configured: boolean;
  readonly topEvents: readonly EventCount[];
  readonly topScreens: readonly ScreenView[];
}

interface FunnelStep {
  readonly name: string;
  readonly count: number;
  readonly conversionFromStart: number;
  readonly conversionFromPrev: number;
}

interface FunnelResponse {
  readonly configured: boolean;
  readonly steps: readonly FunnelStep[];
}

const fmtInt = new Intl.NumberFormat('en-US');

const eventCols: readonly Column<EventCount>[] = [
  { key: 'event', header: 'Event', render: (r) => <code style={{ fontFamily: 'SF Mono, Menlo, Consolas, monospace' }}>{r.event}</code> },
  { key: 'count', header: 'Count', width: '120px', align: 'right', render: (r) => fmtInt.format(r.count) },
];

const screenCols: readonly Column<ScreenView>[] = [
  { key: 'screen', header: 'Screen', render: (r) => <code style={{ fontFamily: 'SF Mono, Menlo, Consolas, monospace' }}>{r.screen}</code> },
  { key: 'count', header: 'Views', width: '120px', align: 'right', render: (r) => fmtInt.format(r.count) },
];

function NotConfigured({ source }: { readonly source: string }) {
  return (
    <div className="nw-placeholder">
      <h2 className="nw-placeholder__title">{source} not configured</h2>
      <p>
        Set the {source} env vars on admin-api to populate this page.
        Until then the data source returns an empty response and the
        dashboard renders this placeholder.
      </p>
    </div>
  );
}

export function Analytics() {
  const dauMau = useQuery({
    queryKey: queryKeys.analytics.dauMau('30d'),
    queryFn: async () => {
      const res = await apiClient.get<DauMauResponse>('/owner/analytics/dau-mau?days=30');
      return res.data;
    },
  });
  const events = useQuery({
    queryKey: queryKeys.analytics.events(),
    queryFn: async () => {
      const res = await apiClient.get<EventsResponse>('/owner/analytics/events?days=7');
      return res.data;
    },
  });
  const funnel = useQuery({
    queryKey: queryKeys.analytics.funnel('onboarding'),
    queryFn: async () => {
      const res = await apiClient.get<FunnelResponse>('/owner/analytics/funnels/onboarding?days=30');
      return res.data;
    },
  });

  const configured =
    dauMau.data?.configured || events.data?.configured || funnel.data?.configured;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Analytics</h1>
        <p className="nw-page-header__subtitle">
          DAU/MAU, onboarding funnel, and top events — backed by PostHog HogQL.
        </p>
      </header>

      {!dauMau.isLoading && !configured && <NotConfigured source="PostHog" />}

      {dauMau.data?.configured && (
        <section className="nw-panel">
          <header className="nw-panel__header">
            <h2 className="nw-panel__title">DAU vs MAU · last 30 days</h2>
          </header>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={[...dauMau.data.points]}>
              <defs>
                <linearGradient id="dauFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff5c28" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ff5c28" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--divider)" strokeOpacity={0.4} />
              <XAxis dataKey="date" stroke="var(--text-muted)" tick={{ fontSize: 11 }} />
              <YAxis stroke="var(--text-muted)" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'var(--card)',
                  border: '0.5px solid var(--card-border)',
                  borderRadius: 10,
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="dau" stroke="#ff5c28" fill="url(#dauFill)" name="DAU" />
              <Area type="monotone" dataKey="mau" stroke="#4a9eff" fillOpacity={0} name="MAU" />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      )}

      {dauMau.data?.configured && (
        <section className="nw-panel">
          <header className="nw-panel__header">
            <h2 className="nw-panel__title">Stickiness (DAU / MAU)</h2>
          </header>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={[...dauMau.data.points]}>
              <CartesianGrid stroke="var(--divider)" strokeOpacity={0.4} />
              <XAxis dataKey="date" stroke="var(--text-muted)" tick={{ fontSize: 11 }} />
              <YAxis stroke="var(--text-muted)" tick={{ fontSize: 11 }} unit="%" />
              <Tooltip
                contentStyle={{
                  background: 'var(--card)',
                  border: '0.5px solid var(--card-border)',
                  borderRadius: 10,
                }}
              />
              <Line type="monotone" dataKey="stickiness" stroke="#ff5c28" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      {funnel.data?.configured && funnel.data.steps.length > 0 && (
        <section className="nw-panel">
          <header className="nw-panel__header">
            <h2 className="nw-panel__title">Onboarding funnel · last 30 days</h2>
          </header>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {funnel.data.steps.map((step) => {
              const pct = Math.min(100, step.conversionFromStart);
              return (
                <div key={step.name}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 'var(--fs-sm)',
                      marginBottom: 4,
                    }}
                  >
                    <code style={{ color: 'var(--text-primary)' }}>{step.name}</code>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {fmtInt.format(step.count)} ·{' '}
                      <b style={{ color: 'var(--accent)' }}>{pct.toFixed(1)}%</b>
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: 'var(--tag-bg)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: 'var(--accent)',
                        transition: 'width 200ms ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {events.data?.configured && (
        <div className="nw-panel-row">
          <section className="nw-panel">
            <header className="nw-panel__header">
              <h2 className="nw-panel__title">Top events · last 7 days</h2>
            </header>
            <DataTable
              columns={eventCols}
              rows={events.data.topEvents}
              rowKey={(r) => r.event}
              emptyMessage="No events."
            />
          </section>
          <section className="nw-panel">
            <header className="nw-panel__header">
              <h2 className="nw-panel__title">Top screens · last 7 days</h2>
            </header>
            <DataTable
              columns={screenCols}
              rows={events.data.topScreens}
              rowKey={(r) => r.screen}
              emptyMessage="No screens."
            />
          </section>
        </div>
      )}
    </>
  );
}
